import { UnresolvedPresetRange } from '@amzn/storm-ui-date-picker';
import dt from '@urc-ui/components/date-input/utils/date-time-fns';

export const ISO_DATE_FORMAT = 'yyyy-MM-dd';
export const CUSTOM_PERIOD_ID = 'CUSTOM';

export enum PRESET_PERIOD {
  TODAY = 'TODAY',
  YESTERDAY = 'YESTERDAY',
  LAST_7_DAYS = 'LAST_7_DAYS',
  THIS_WEEK = 'THIS_WEEK',
  WEEK_TO_DATE = 'WEEK_TO_DATE',
  LAST_WEEK = 'LAST_WEEK',
  THIS_MONTH = 'THIS_MONTH',
  MONTH_TO_DATE = 'MONTH_TO_DATE',
  LAST_30_DAYS = 'LAST_30_DAYS',
  LAST_MONTH = 'LAST_MONTH',
  LAST_90_DAYS = 'LAST_90_DAYS',
  THIS_QUARTER = 'THIS_QUARTER',
  LAST_QUARTER = 'LAST_QUARTER',
  THIS_YEAR = 'THIS_YEAR',
  LAST_YEAR = 'LAST_YEAR',
  CUSTOM = 'CUSTOM'
}

/**
 * List of presets that are available. Any new preset added here should also have a corresponding
 * entry in PRESET_DURATION_MAP
 */
export const PRESETS_MAP: Omit<UnresolvedPresetRange, 'label'>[] = [
  {
    period: PRESET_PERIOD.TODAY,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay)
  },
  {
    period: PRESET_PERIOD.YESTERDAY,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.addDays(-1)),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay, dt.addDays(-1))
  },
  {
    period: PRESET_PERIOD.LAST_7_DAYS,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.addDays(-7)),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay, dt.addDays(-1))
  },
  {
    period: PRESET_PERIOD.THIS_WEEK,
    start: (today, zone, locale) => dt.compose({ today, zone, locale }, dt.startOfDay, dt.startOfWeek),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay)
  },
  {
    period: PRESET_PERIOD.LAST_WEEK,
    start: (today, zone, locale) => dt.compose({ today, zone, locale }, dt.startOfDay, dt.addWeeks(-1), dt.startOfWeek),
    end: (today, zone, locale) => dt.compose({ today, zone, locale }, dt.endOfDay, dt.addWeeks(-1), dt.endOfWeek)
  },
  {
    period: PRESET_PERIOD.LAST_30_DAYS,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.addDays(-30)),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay, dt.addDays(-1))
  },
  {
    period: PRESET_PERIOD.THIS_MONTH,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.startOfMonth),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay)
  },
  {
    period: PRESET_PERIOD.LAST_MONTH,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.addMonths(-1), dt.startOfMonth),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay, dt.addMonths(-1), dt.endOfMonth)
  },
  {
    period: PRESET_PERIOD.LAST_90_DAYS,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.addDays(-90)),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay, dt.addDays(-1))
  },
  {
    period: PRESET_PERIOD.THIS_QUARTER,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.startOfQuarter),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay)
  },
  {
    period: PRESET_PERIOD.LAST_QUARTER,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.addQuarters(-1), dt.startOfQuarter),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay, dt.addQuarters(-1), dt.endOfQuarter)
  },
  {
    period: PRESET_PERIOD.THIS_YEAR,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.startOfYear),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay)
  },
  {
    period: PRESET_PERIOD.LAST_YEAR,
    start: (today, zone) => dt.compose({ today, zone }, dt.startOfDay, dt.addYears(-1), dt.startOfYear),
    end: (today, zone) => dt.compose({ today, zone }, dt.endOfDay, dt.addYears(-1), dt.endOfYear)
  }
];

/**
 *  Map keeping track of the max duration for each preset_period. This is
 *  used when trying to identify the right presets given a reportingWindow.
 *  When adding a new preset, this map should be updated also to include the
 *  duration.
 */
export const PRESET_DURATION_MAP = new Map<PRESET_PERIOD, number>([
  [PRESET_PERIOD.TODAY, 1],
  [PRESET_PERIOD.YESTERDAY, 1],
  [PRESET_PERIOD.LAST_7_DAYS, 7],
  [PRESET_PERIOD.THIS_WEEK, 7],
  [PRESET_PERIOD.LAST_WEEK, 7],
  [PRESET_PERIOD.LAST_30_DAYS, 30],
  [PRESET_PERIOD.THIS_MONTH, 30],
  [PRESET_PERIOD.LAST_MONTH, 30],
  [PRESET_PERIOD.LAST_90_DAYS, 90],
  [PRESET_PERIOD.THIS_QUARTER, 90],
  [PRESET_PERIOD.LAST_QUARTER, 90],
  [PRESET_PERIOD.THIS_YEAR, 365],
  [PRESET_PERIOD.LAST_YEAR, 365]
]);
